import { useCallback, useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../../lib/api";
import { usePoll } from "../../hooks/usePoll";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

function Concept({ c }) {
  const [src, setSrc] = useState(null);
  useEffect(() => { api.get(`/cover-concepts/${c.id}/image`, { responseType: "blob" }).then((r) => setSrc(URL.createObjectURL(r.data))); }, [c.id]);
  return (
    <div className="panel overflow-hidden" data-testid={`concept-${c.id}`}>
      <div className={`bg-slate-900 ${c.edition === "audiobook" ? "aspect-square" : "aspect-[2/3]"}`}>{src && <img src={src} alt="Cover concept" className="w-full h-full object-cover" />}</div>
      <div className="p-3"><p className="text-[11px] font-mono text-amber-400/80">{c.edition} · {c.width}×{c.height}</p><p className="text-xs text-slate-500 line-clamp-2 mt-1">{c.prompt}</p><p className="text-[10px] text-slate-600 mt-1">{c.label} · {fmtDate(c.created_at)}</p>
        {src && <a href={src} download={`cover_concept_${c.edition}.png`} className="text-xs text-amber-400" data-testid={`concept-download-${c.id}`}>Download PNG</a>}</div>
    </div>
  );
}

export default function CoversTab({ book }) {
  const [list, setList] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [edition, setEdition] = useState("ebook");
  const [job, setJob] = useState(null);
  const load = useCallback(() => api.get(`/books/${book.id}/cover-concepts`).then((r) => setList(r.data)), [book.id]);
  useEffect(() => { load(); }, [load]);
  const poll = async () => {
    const { data } = await api.get(`/media-jobs/${job.id}`);
    setJob(data);
    if (data.status === "completed") { toast.success("Cover concept ready"); load(); }
    if (data.status === "failed") toast.error(data.error || "Generation failed");
  };
  usePoll(poll, job && ["queued", "running"].includes(job.status), 3000);
  const gen = async () => { try { const { data } = await api.post(`/books/${book.id}/cover-concepts`, { prompt, edition, resolution: "2K" }); setJob({ id: data.id, status: "queued" }); } catch (e) { toast.error(errMsg(e)); } };
  const busy = job && ["queued", "running"].includes(job.status);
  return (
    <div className="space-y-6">
      <div className="panel p-5 space-y-3" data-testid="cover-concept-form">
        <p className="eyebrow">AI cover concepts · Nano Banana Pro</p>
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe the mood, setting, colours and imagery — e.g. a lone lighthouse on a storm-dark coast, golden beam, painterly, no text" className="min-h-[80px]" data-testid="cover-prompt" />
        <div className="flex flex-wrap gap-2 items-center">
          {[["ebook", "Ebook 2:3"], ["print_front", "Print front 2:3"], ["audiobook", "Audiobook 1:1"]].map(([k, l]) => <button key={k} onClick={() => setEdition(k)} className={`text-xs px-3 py-1.5 rounded-lg border ${edition === k ? "border-amber-500 text-amber-300" : "border-slate-700 text-slate-400"}`} data-testid={`cover-edition-${k}`}>{l}</button>)}
          <Button onClick={gen} disabled={busy} className="ml-auto bg-amber-600 hover:bg-amber-500" data-testid="generate-cover-button"><Sparkles size={14} className="mr-1" />{busy ? "Generating (up to a minute)…" : "Generate concept"}</Button>
        </div>
        <p className="text-[11px] text-slate-500">Concepts are artwork ideas only: they are not sized, bled or validated for KDP and are kept separate from your uploaded cover artwork. Image generation is charged to your Universal Key balance. Pick a concept as front artwork in the Paperback or Hardcover tab to build a sized wraparound cover PDF.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="concept-gallery">{list.map((c) => <Concept key={c.id} c={c} />)}</div>
    </div>
  );
}
